import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Tab, Tabs } from '@mui/material';
import React, { SetStateAction, useEffect, useState } from 'react';
import { Control, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ERole } from '../../../../../shared/enums/user/ERole';
import { ETheme } from '../../../../../shared/enums/user/ETheme';
import { handleError } from '../../../../../shared/functions/handleError';
import { IFormField } from '../../../../../shared/interfaces/forms/IForms';
import { IUser } from '../../../../../shared/interfaces/user/IUser';
import { darkPalette, lightPalette } from '../../../../../shared/mui/palettes';
import { RootState } from '../../../../../store/store';
import { UserEmployeeService } from '../../../../user/employees/service';
import { AdminEmployeeService } from '../../service';
import AccountAccess from './AccountAccess';
import Documents from './Documents';
import LeaveDaysBalance from './LeaveDaysBalance';
import PersonalInformation from './PersonalInformation';
import ProfessionalInformation from './ProfessionalInformation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ padding: { xs: '24px 0', md: '24px' } }}>{children}</Box>
      )}
    </div>
  );
}

export function a11yProps(index: number, hasError?: boolean) {
  return {
    id: `simple-tab-${index}`,
    error: `${hasError}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface IProps {
  control: Control<IUser>;
  isLoading?: boolean;
  isUpdate?: boolean;
  errors: string[];
  watch: UseFormWatch<IUser>;
  file: File | null;
  files: FileList | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
  setFiles: React.Dispatch<SetStateAction<FileList | null>>;
}

const errorList = {
  1: [
    'email',
    'firstName',
    'lastName',
    'birthday',
    'gender',
    'religion',
    'address',
    'city',
    'country',
    'phoneNumber',
    'zipCode',
    'theme',
    'language',
    'nbChildren',
    'position',
  ],
  2: [
    'employeeType',
    'workMode',
    'employeeContract',
    'contractEndDate',
    'skills',
  ],
  3: ['linkedin', 'gitHub'],
  4: [
    'vacationDays',
    'carryOverVacationDays',
    'businessTravelLeaveDays',
    'sickDays',
  ],
};

const EmployeeTabs = ({
  control,
  isLoading = false,
  isUpdate = false,
  watch,
  file,
  files,
  setFile,
  setFiles,
  errors,
}: IProps) => {
  const { t } = useTranslation();
  const { tab, id } = useParams();
  const [value, setValue] = React.useState(tab ? parseInt(tab) - 1 : 0);
  const navigate = useNavigate();
  const {
    theme,
    id: userId,
    role,
  } = useSelector((state: RootState) => state?.auth?.userData) as IUser;
  const [formFields, setFormFields] = useState<{
    1: IFormField[];
    2: IFormField[];
    3: IFormField[];
    4: IFormField[];
    5: IFormField[];
  }>({ 1: [], 2: [], 3: [], 4: [], 5: [] });
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Navigate with the correct 1-based index for the URL
    navigate(
      !!id
        ? `/${role === ERole.USER ? 'user' : 'admin'}/employee/${id}/${newValue + 1}`
        : `/${role === ERole.USER ? 'user' : 'admin'}/employees/create/${newValue + 1}`,
    );
  };

  const checkStepErrors = (stepId: number): boolean => {
    const errorsForStep = errorList[
      stepId as keyof typeof errorList
    ] as string[];
    return !!errors?.filter((error) => errorsForStep?.includes(error))?.length;
  };

  useEffect(() => {
    if (role === ERole.USER) {
      UserEmployeeService.getSelfUserForm(Number(id ?? userId))
        .then(setFormFields)
        .catch(handleError);
    } else {
      AdminEmployeeService.getFormFields(Number(id ?? userId))
        .then(setFormFields)
        .catch(handleError);
    }
  }, [id, userId, role]);
  return (
    <Card>
      <Box
        sx={{
          borderBottom: 1,
          borderColor:
            theme === ETheme.LIGHT
              ? lightPalette.primary.light
              : darkPalette.background.light,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          aria-label="basic tabs example"
        >
          <Tab
            icon={<Person2OutlinedIcon />}
            label={t('employee.personalInformation')}
            {...a11yProps(0, checkStepErrors(1))}
          />
          <Tab
            icon={<BusinessCenterOutlinedIcon />}
            label={t('employee.professionalInformation')}
            {...a11yProps(1, checkStepErrors(2))}
          />
          <Tab
            icon={<ArticleOutlinedIcon />}
            label={t('employee.documents')}
            {...a11yProps(2, checkStepErrors(3))}
          />
          <Tab
            icon={<LockOutlinedIcon />}
            label={t('employee.accountAccess')}
            {...a11yProps(3, checkStepErrors(4))}
          />
          {role !== ERole.USER && (
            <Tab
              icon={<BeachAccessOutlinedIcon />}
              label={t('employee.leaveDaysBalance')}
              {...a11yProps(4, checkStepErrors(5))}
            />
          )}
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <PersonalInformation control={control} formFields={formFields[1]} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <ProfessionalInformation
          control={control}
          watch={watch}
          formFields={formFields[2]}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Documents
          watch={watch}
          formFields={formFields[3]}
          file={file}
          files={files}
          setFile={setFile}
          setFiles={setFiles}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <AccountAccess control={control} formFields={formFields[4]} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <LeaveDaysBalance control={control} formFields={formFields[5]} />
      </CustomTabPanel>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant={'contained'}
          color={'secondary'}
          size={'small'}
          type={'button'}
          onClick={() =>
            navigate(`/${role === ERole.USER ? 'user' : 'admin'}/employees`)
          }
        >
          {t('button.cancel')}
        </Button>
        {!isUpdate && value < 4 && (
          <Button
            variant={'contained'}
            size={'small'}
            type={'button'}
            onClick={(e) => handleChange(e, Number(tab))}
            sx={{ marginLeft: '10px' }}
          >
            {t('button.next')}
          </Button>
        )}
        {(isUpdate || value === 4) && (
          <LoadingButton
            loading={isLoading}
            variant={'contained'}
            type={'submit'}
            size={'small'}
            sx={{ marginLeft: '10px' }}
          >
            {t(isUpdate ? 'button.update' : 'button.create')}
          </LoadingButton>
        )}
      </Box>
    </Card>
  );
};

export default EmployeeTabs;
